import { Observable, Subscriber, Subscription } from 'rxjs'
import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { WebsocketErrorMessage, WebsocketMessage, WebsocketMessageType } from './Types/Types'
import { MockMessage, MockWebSocket } from './MockWebsocket'
import { IsStringEmpty } from '../../../../NonReact/utils/GlobalFunctions'

/**
 * AkixiWebSocket class
 *
 * @template InboundMessages - The type of inbound messages
 * @template OutboundMessages - The type of outbound messages
 */
export class AkixiWebSocket<
    InboundMessages extends WebsocketMessage<unknown>,
    OutboundMessages extends WebsocketMessage<unknown>
> {
    public readonly webSocket: WebSocket
    private subscription: Nullable<Subscription>

    constructor(url?: URLConstructor, protocols?: string | string[]) {
        if (url) {
            const superURL: Nullable<string> = url.constructURL()

            this.webSocket = new WebSocket(superURL, protocols)
        } else {
            this.webSocket = new MockWebSocket<InboundMessages, OutboundMessages>()
        }
    }

    /**
     * Receive messages from the WebSocket server via a RxJS Observable and parse as JSON.
     */
    public listenToWebSocket(): Observable<InboundMessages> {
        return new Observable<InboundMessages>((observer: Subscriber<InboundMessages>) => {
            this.webSocket.addEventListener('message', (msg: MessageEvent<string>): void => {
                // Try to parse the message as JSON
                try {
                    const inboundMessage: InboundMessages | WebsocketErrorMessage = JSON.parse(msg.data) as
                        | InboundMessages
                        | WebsocketErrorMessage

                    if (inboundMessage.type === WebsocketMessageType.ERROR) {
                        observer.error(new Error(inboundMessage.data.message))
                    } else {
                        observer.next(inboundMessage)
                    }
                } catch {
                    observer.error(new Error('Failed to parse message as JSON'))
                }
            })

            this.webSocket.addEventListener('error', (error: Event): void => observer.error(error))
            this.webSocket.addEventListener('close', (): void => observer.complete())

            return this.webSocket.close.bind(this.webSocket)
        })
    }

    public subscribe(
        next?: (value: InboundMessages) => void,
        error?: (error: Error) => void,
        complete?: () => void
    ): Subscription {
        this.subscription = this.listenToWebSocket().subscribe(next, error, complete)

        return this.subscription
    }

    public unsubscribe(): void {
        if (this.webSocket.readyState === WebSocket.OPEN) {
            this.subscription?.unsubscribe()
        }
    }

    /**
     * Send a message to the WebSocket server
     */
    public sendMessage(message: OutboundMessages): void {
        if (this.webSocket.readyState === WebSocket.OPEN) {
            try {
                this.webSocket.send(JSON.stringify(message))
            } catch (e) {
                // Ignore
            }
        } else {
            setTimeout((): void => this.sendMessage(message), 100)
        }
    }

    /**
     * Mock a message from the server
     *
     * @param mockMessages - The mock messages to add
     */
    public addMockMessages(
        ...mockMessages: MockMessage<InboundMessages | WebsocketErrorMessage, OutboundMessages>[]
    ): void {
        if (IsStringEmpty(this.webSocket.url)) {
            mockMessages.forEach(
                (mockMessage: MockMessage<InboundMessages | WebsocketErrorMessage, OutboundMessages>) =>
                    (
                        this.webSocket as MockWebSocket<InboundMessages | WebsocketErrorMessage, OutboundMessages>
                    ).addMockMessage(mockMessage)
            )
        }
    }

    public isWebsocketOpen(): boolean {
        return this.webSocket.readyState === WebSocket.OPEN
    }

    public static newInstance<
        InboundMessages extends WebsocketMessage<unknown>,
        OutboundMessages extends WebsocketMessage<unknown>
    >(url?: URLConstructor, protocols?: string | string[]): AkixiWebSocket<InboundMessages, OutboundMessages> {
        return new AkixiWebSocket<InboundMessages, OutboundMessages>(url, protocols)
    }
}
